import loadScript from '../util/loader';
import * as a11y from '../vendor/theme-scripts/theme-a11y';

import {LoadVideoVimeo} from './load-video-vimeo';
import {LoadVideoYT} from './load-video-youtube';

const selectors = {
  popupContainer: '.pswp',
  popupCloseBtn: '.pswp__custom-close',
  popupIframe: 'iframe, video',
  popupCustomIframe: '.pswp__custom-iframe',
  popupThumbs: '.pswp__thumbs',
  dataOptionClasses: 'data-pswp-option-classes',
  popupButtons: '.pswp__button, .pswp__caption-close',
  dataVideoType: 'data-video-type',
};

const classes = {
  current: 'is-current',
  customLoader: 'pswp--custom-loader',
  customOpen: 'pswp--custom-opening',
  loader: 'pswp__loader',
  popupCloseButton: 'pswp__button--close',
};

const loaderHTML = `<div class="${classes.loader}"><div class="loader pswp__loader-line"><div class="loader-indeterminate"></div></div></div>`;

class LoadPhotoswipe {
  constructor(items, options = '') {
    this.items = items;
    this.pswpElement = document.querySelectorAll(selectors.popupContainer)[0];
    this.popup = null;
    this.popupThumbs = null;
    this.isVideo = false;
    this.popupThumbsContainer = this.pswpElement.querySelector(selectors.popupThumbs);
    this.closeBtn = this.pswpElement.querySelector(selectors.popupCloseBtn);
    this.a11y = a11y;

    const defaultOptions = {
      history: false,
      focus: false,
      mainClass: '',
    };
    this.options = options !== '' ? options : defaultOptions;

    this.init();
  }

  init() {
    this.pswpElement.classList.add(classes.customOpen);

    this.initLoader();

    loadScript({url: window.theme.assets.photoswipe})
      .then(() => this.loadPopup())
      .catch((e) => console.error(e));
  }

  initLoader() {
    if (this.pswpElement.classList.contains(classes.customLoader) && this.options !== '' && this.options.mainClass) {
      this.pswpElement.setAttribute(selectors.dataOptionClasses, this.options.mainClass);
      let loaderElem = document.createElement('div');
      loaderElem.innerHTML = loaderHTML;
      loaderElem = loaderElem.firstChild;
      this.pswpElement.appendChild(loaderElem);
    } else {
      this.pswpElement.setAttribute(selectors.dataOptionClasses, '');
    }
  }

  loadPopup() {
    const PhotoSwipe = window.themePhotoswipe.PhotoSwipe.default;
    const PhotoSwipeUI = window.themePhotoswipe.PhotoSwipeUI.default;

    if (this.pswpElement.classList.contains(classes.customLoader)) {
      this.pswpElement.classList.remove(classes.customLoader);
    }

    this.pswpElement.classList.remove(classes.customOpen);

    this.popup = new PhotoSwipe(this.pswpElement, PhotoSwipeUI, this.items, this.options);
    this.popup.init();

    this.initVideo();

    this.thumbsActions();

    if (this.isVideo) {
      this.hideUnusedButtons();
    }

    setTimeout(() => {
      this.a11y.trapFocus(this.pswpElement, {
        elementToFocus: this.closeBtn,
      });
    }, 200);

    this.popup.listen('close', () => this.onClose());

    if (this.closeBtn) {
      this.closeBtn.addEventListener('click', () => this.popup.close());
    }
  }

  initVideo() {
    const videoContainer = this.pswpElement.querySelector(selectors.popupCustomIframe);
    if (videoContainer) {
      const videoType = videoContainer.getAttribute(selectors.dataVideoType);
      this.isVideo = true;

      if (videoType == 'youtube') {
        new LoadVideoYT(videoContainer.parentElement);
      } else if (videoType == 'vimeo') {
        new LoadVideoVimeo(videoContainer.parentElement);
      }
    }
  }

  thumbsActions() {
    if (this.popupThumbsContainer && this.popupThumbsContainer.firstChild) {
      this.popupThumbsContainer.addEventListener('wheel', (e) => this.stopDisabledScroll(e));
      this.popupThumbsContainer.addEventListener('mousewheel', (e) => this.stopDisabledScroll(e));
      this.popupThumbsContainer.addEventListener('DOMMouseScroll', (e) => this.stopDisabledScroll(e));

      this.popupThumbs = this.pswpElement.querySelectorAll(`${selectors.popupThumbs} > *`);
      this.popupThumbs.forEach((element, i) => {
        element.addEventListener('click', (e) => {
          e.preventDefault();
          element.parentElement.querySelector(`.${classes.current}`).classList.remove(classes.current);
          element.classList.add(classes.current);
          this.popup.goTo(i);
        });
      });

      this.popup.listen('imageLoadComplete', () => this.setCurrentThumb());
      this.popup.listen('beforeChange', () => this.setCurrentThumb());
    }
  }

  hideUnusedButtons() {
    const buttons = this.pswpElement.querySelectorAll(selectors.popupButtons);
    buttons.forEach((element) => {
      if (!element.classList.contains(classes.popupCloseButton)) {
        element.style.display = 'none';
      }
    });
  }

  stopDisabledScroll(e) {
    e.stopPropagation();
  }

  onClose() {
    const popupIframe = this.pswpElement.querySelector(selectors.popupIframe);
    if (popupIframe) {
      popupIframe.parentNode.removeChild(popupIframe);
    }

    if (this.popupThumbsContainer && this.popupThumbsContainer.firstChild) {
      while (this.popupThumbsContainer.firstChild) this.popupThumbsContainer.removeChild(this.popupThumbsContainer.firstChild);
    }

    this.pswpElement.setAttribute(selectors.dataOptionClasses, '');
    const loaderElem = this.pswpElement.querySelector(`.${classes.loader}`);
    if (loaderElem) {
      this.pswpElement.removeChild(loaderElem);
    }

    this.a11y.removeTrapFocus();

    if (window.accessibility.lastElement) {
      setTimeout(() => {
        window.accessibility.lastElement.focus();
      }, 200);
    }
  }

  setCurrentThumb() {
    const lastCurrentThumb = this.pswpElement.querySelector(`${selectors.popupThumbs} > .${classes.current}`);
    if (lastCurrentThumb) {
      lastCurrentThumb.classList.remove(classes.current);
    }

    if (!this.popupThumbs) return;
    const currentThumb = this.popupThumbs[this.popup.getCurrentIndex()];
    currentThumb.classList.add(classes.current);
    this.scrollThumbs(currentThumb);
  }

  scrollThumbs(currentThumb) {
    const thumbsContainerLeft = this.popupThumbsContainer.scrollLeft;
    const thumbsContainerWidth = this.popupThumbsContainer.offsetWidth;
    const thumbsContainerPos = thumbsContainerLeft + thumbsContainerWidth;
    const currentThumbLeft = currentThumb.offsetLeft;
    const currentThumbWidth = currentThumb.offsetWidth;
    const currentThumbPos = currentThumbLeft + currentThumbWidth;

    if (thumbsContainerPos <= currentThumbPos || thumbsContainerPos > currentThumbLeft) {
      const currentThumbMarginLeft = parseInt(window.getComputedStyle(currentThumb).marginLeft);
      this.popupThumbsContainer.scrollTo({
        top: 0,
        left: currentThumbLeft - currentThumbMarginLeft,
        behavior: 'smooth',
      });
    }
  }
}

export {LoadPhotoswipe};
